import { useState, useMemo, Fragment } from 'react'
import {
  ShieldCheck, Users as UsersIcon, ArrowUpDown,
  ChevronDown, ChevronRight, Eye, FolderCog, ClipboardCheck, Send, UserX,
  Check,
} from 'lucide-react'

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Name A→Z' },
  { value: 'name_desc', label: 'Name Z→A' },
  { value: 'last_login_desc', label: 'Last Login (Recent)' },
  { value: 'last_login_asc', label: 'Last Login (Oldest)' },
]

export { SORT_OPTIONS }

// Maximum rows visible before section becomes scrollable
const MAX_VISIBLE_ROWS = 10
const ROW_HEIGHT = 64 // approximate height per row in pixels

function sortUsers(users, sortKey) {
  const sorted = [...users]
  switch (sortKey) {
    case 'name_asc':
      return sorted.sort((a, b) => (a.username || a.email || '').localeCompare(b.username || b.email || ''))
    case 'name_desc':
      return sorted.sort((a, b) => (b.username || b.email || '').localeCompare(a.username || a.email || ''))
    case 'last_login_desc':
      return sorted.sort((a, b) => {
        if (!a.last_login && !b.last_login) return 0
        if (!a.last_login) return 1
        if (!b.last_login) return -1
        return new Date(b.last_login) - new Date(a.last_login)
      })
    case 'last_login_asc':
      return sorted.sort((a, b) => {
        if (!a.last_login && !b.last_login) return 0
        if (!a.last_login) return 1
        if (!b.last_login) return -1
        return new Date(a.last_login) - new Date(b.last_login)
      })
    default:
      return sorted
  }
}

/**
 * Section definitions for user categorization
 */
const SECTIONS = [
  { key: 'admin', label: 'Administrators', icon: ShieldCheck, color: 'purple' },
  { key: 'template_manager', label: 'Template Managers', icon: FolderCog, color: 'indigo' },
  { key: 'reviewer', label: 'Approvers', icon: ClipboardCheck, color: 'green' },
  { key: 'sender', label: 'Reviewers', icon: Send, color: 'amber' },
  { key: 'reader', label: 'Readers', icon: Eye, color: 'emerald' },
  { key: 'user', label: 'Users', icon: UsersIcon, color: 'blue' },
  { key: 'deactivated', label: 'Deactivated', icon: UserX, color: 'gray' },
]

const SECTION_COLORS = {
  purple: {
    bg: 'bg-purple-50/80 dark:bg-purple-900/20',
    text: 'text-purple-700 dark:text-purple-300',
    border: 'border-purple-200 dark:border-purple-800',
    badge: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
  },
  indigo: {
    bg: 'bg-indigo-50/80 dark:bg-indigo-900/20',
    text: 'text-indigo-700 dark:text-indigo-300',
    border: 'border-indigo-200 dark:border-indigo-800',
    badge: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300',
  },
  green: {
    bg: 'bg-green-50/80 dark:bg-green-900/20',
    text: 'text-green-700 dark:text-green-300',
    border: 'border-green-200 dark:border-green-800',
    badge: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  },
  amber: {
    bg: 'bg-amber-50/80 dark:bg-amber-900/20',
    text: 'text-amber-700 dark:text-amber-300',
    border: 'border-amber-200 dark:border-amber-800',
    badge: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
  },
  emerald: {
    bg: 'bg-emerald-50/80 dark:bg-emerald-900/20',
    text: 'text-emerald-700 dark:text-emerald-300',
    border: 'border-emerald-200 dark:border-emerald-800',
    badge: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300',
  },
  blue: {
    bg: 'bg-blue-50/80 dark:bg-blue-900/20',
    text: 'text-blue-700 dark:text-blue-300',
    border: 'border-blue-200 dark:border-blue-800',
    badge: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
  },
  gray: {
    bg: 'bg-gray-100/80 dark:bg-gray-800/50',
    text: 'text-gray-600 dark:text-gray-400',
    border: 'border-gray-200 dark:border-gray-700',
    badge: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
  },
}

/**
 * Checkbox component for consistent styling
 */
function Checkbox({ checked, indeterminate, onChange, size = 'md', className = '' }) {
  const sizeClasses = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'
  const iconSize = size === 'sm' ? 'w-2.5 h-2.5' : 'w-3 h-3'

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        onChange?.()
      }}
      className={`${sizeClasses} rounded-md border-2 flex items-center justify-center transition-all duration-150 ${className} ${
        checked
          ? 'bg-navy-700 border-navy-700 dark:bg-blue-600 dark:border-blue-600 shadow-sm'
          : indeterminate
            ? 'border-navy-500 dark:border-blue-500 bg-navy-50 dark:bg-blue-900/30'
            : 'border-gray-300 dark:border-gray-600 hover:border-navy-400 dark:hover:border-blue-500 bg-white dark:bg-gray-800'
      }`}
      role="checkbox"
      aria-checked={checked ? 'true' : indeterminate ? 'mixed' : 'false'}
    >
      {checked && <Check className={`${iconSize} text-white`} strokeWidth={3} />}
      {indeterminate && !checked && (
        <div className={`${size === 'sm' ? 'w-1.5' : 'w-2'} h-0.5 bg-navy-600 dark:bg-blue-400 rounded-full`} />
      )}
    </button>
  )
}

/**
 * Reusable sectionized user table with role-based sections, checkbox selection, and scrollable sections.
 * Uses a single table structure with colgroup for consistent column alignment.
 */
export default function SectionizedUserTable({
  users = [],
  columns = [],
  currentUserId,
  sortable = true,
  collapsible = true,
  renderActions,
  emptyMessage = 'No users found',
  compact = false,
  onRowClick,
  rowClassName,
  sectionCounts = true,
  selectable = false,
  selectedIds = new Set(),
  onSelectionChange,
  renderBulkActions,
  externalSortKey,
  onSortChange,
  tableMinWidthClass = 'min-w-[1060px]',
  actionsHeaderClassName = 'w-28 text-right',
  actionsCellClassName = 'text-right',
  getSectionKey,
  maxVisibleRows = MAX_VISIBLE_ROWS,
}) {
  const [internalSortKey, setInternalSortKey] = useState('name_asc')
  const sortKey = externalSortKey !== undefined ? externalSortKey : internalSortKey
  const setSortKey = onSortChange || setInternalSortKey

  // Collapsed state for each section
  const [collapsedSections, setCollapsedSections] = useState({})

  // Group users by section - handle various is_active representations
  const sectionedUsers = useMemo(() => {
    const grouped = {}
    SECTIONS.forEach(s => { grouped[s.key] = [] })

    // Role to section key mapping - handles variations
    const roleToSection = {
      'admin': 'admin',
      'administrator': 'admin',
      'template_manager': 'template_manager',
      'templatemanager': 'template_manager',
      'reviewer': 'reviewer',  // Approver role
      'approver': 'reviewer',
      'sender': 'sender',      // Reviewer role
      'user': 'user',
      'reader': 'reader',
    }

    users.forEach(user => {
      let key
      if (getSectionKey) {
        key = getSectionKey(user)
      } else {
        // Check if deactivated - handle boolean false, 0, undefined with last_login
        const isDeactivated = user.is_active === false || user.is_active === 0
        if (isDeactivated) {
          key = 'deactivated'
        } else {
          // Normalize role to lowercase and map to section key
          const role = String(user.role || 'user').toLowerCase().replace(/[^a-z_]/g, '')
          key = roleToSection[role] || 'user'
        }
      }

      if (grouped[key]) {
        grouped[key].push(user)
      } else {
        // Fallback to 'user' section for unknown roles
        grouped.user.push(user)
      }
    })

    return grouped
  }, [users, getSectionKey])

  // Sort users within each section
  const sortedSectionedUsers = useMemo(() => {
    const sorted = {}
    Object.entries(sectionedUsers).forEach(([key, sectionUsers]) => {
      sorted[key] = sortUsers(sectionUsers, sortKey)
    })
    return sorted
  }, [sectionedUsers, sortKey])

  // Get all visible users for selection calculations
  const allVisibleUsers = useMemo(() => {
    return Object.values(sortedSectionedUsers).flat()
  }, [sortedSectionedUsers])

  // Selection helpers
  const allSelected = allVisibleUsers.length > 0 && allVisibleUsers.every(u => selectedIds.has(u.id))
  const someSelected = allVisibleUsers.some(u => selectedIds.has(u.id))

  const toggleSelectAll = () => {
    if (!onSelectionChange) return
    if (allSelected) {
      onSelectionChange(new Set())
    } else {
      onSelectionChange(new Set(allVisibleUsers.map(u => u.id)))
    }
  }

  const toggleSelectUser = (userId) => {
    if (!onSelectionChange) return
    const next = new Set(selectedIds)
    if (next.has(userId)) {
      next.delete(userId)
    } else {
      next.add(userId)
    }
    onSelectionChange(next)
  }

  const toggleSelectSection = (sectionKey) => {
    if (!onSelectionChange) return
    const sectionUsers = sortedSectionedUsers[sectionKey] || []
    const allSectionSelected = sectionUsers.length > 0 && sectionUsers.every(u => selectedIds.has(u.id))
    const next = new Set(selectedIds)
    if (allSectionSelected) {
      sectionUsers.forEach(u => next.delete(u.id))
    } else {
      sectionUsers.forEach(u => next.add(u.id))
    }
    onSelectionChange(next)
  }

  const toggleSectionCollapsed = (sectionKey) => {
    setCollapsedSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }))
  }

  const px = compact ? 'px-3' : 'px-4'
  const py = compact ? 'py-2.5' : 'py-3'

  const selectedUsers = useMemo(() => {
    return users.filter(u => selectedIds.has(u.id))
  }, [users, selectedIds])

  // Calculate total column count for colspan
  const totalCols = (selectable ? 1 : 0) + columns.length + (renderActions ? 1 : 0)

  if (users.length === 0) {
    return (
      <div className="p-12 text-center">
        <UsersIcon className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
        <p className="text-sm text-gray-500 dark:text-gray-400">{emptyMessage}</p>
      </div>
    )
  }

  const renderRow = (user) => {
    const isSelf = user.id === currentUserId
    const isSelected = selectedIds.has(user.id)
    const isDeactivated = user.is_active === false || user.is_active === 0

    return (
      <tr
        key={user.id}
        onClick={onRowClick ? () => onRowClick(user) : undefined}
        className={`group ${isDeactivated ? 'opacity-50' : ''} ${
          isSelected ? 'bg-blue-50/60 dark:bg-blue-900/15' : ''
        } hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${
          onRowClick ? 'cursor-pointer' : ''
        } ${rowClassName ? rowClassName(user) : ''}`}
      >
        {selectable && (
          <td className={`${px} ${py} w-10`}>
            <Checkbox
              checked={isSelected}
              onChange={() => toggleSelectUser(user.id)}
              size="sm"
              className={isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'}
            />
          </td>
        )}
        {columns.map(col => (
          <td key={col.key} className={`${px} ${py} ${col.cellClassName || col.className || ''}`}>
            {col.render ? col.render(user, { isSelf }) : (user[col.key] || '')}
          </td>
        ))}
        {renderActions && (
          <td className={`${px} ${py} ${actionsCellClassName}`}>{renderActions(user)}</td>
        )}
      </tr>
    )
  }

  const renderSectionHeader = (section) => {
    const sectionUsers = sortedSectionedUsers[section.key] || []
    if (sectionUsers.length === 0) return null

    const isCollapsed = collapsedSections[section.key] || false
    const colors = SECTION_COLORS[section.color] || SECTION_COLORS.blue
    const Icon = section.icon
    const allSectionSelected = sectionUsers.length > 0 && sectionUsers.every(u => selectedIds.has(u.id))
    const someSectionSelected = sectionUsers.some(u => selectedIds.has(u.id))

    return (
      <tr className={`${colors.bg}`}>
        <td colSpan={totalCols} className={`${px} py-2 border-y ${colors.border}`}>
          <div className="flex items-center gap-2.5">
            {collapsible && (
              <button
                onClick={() => toggleSectionCollapsed(section.key)}
                className={`p-1 rounded-md ${colors.text} hover:bg-white/50 dark:hover:bg-gray-800/50 transition-colors`}
                aria-expanded={!isCollapsed}
                aria-label={isCollapsed ? `Expand ${section.label}` : `Collapse ${section.label}`}
              >
                {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            )}
            {selectable && (
              <Checkbox
                checked={allSectionSelected}
                indeterminate={someSectionSelected && !allSectionSelected}
                onChange={() => toggleSelectSection(section.key)}
                size="sm"
              />
            )}
            <div className={`flex items-center gap-2 ${colors.text}`}>
              <Icon className="w-4 h-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">
                {section.label}
              </span>
              {sectionCounts && (
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${colors.badge}`}>
                  {sectionUsers.length}
                </span>
              )}
            </div>
          </div>
        </td>
      </tr>
    )
  }

  const renderSection = (section) => {
    const sectionUsers = sortedSectionedUsers[section.key] || []
    if (sectionUsers.length === 0) return null

    const isCollapsed = collapsedSections[section.key] || false

    return (
      <Fragment key={section.key}>
        {renderSectionHeader(section)}
        {!isCollapsed && sectionUsers.map(renderRow)}
      </Fragment>
    )
  }

  // Show toolbar only when: (internal sort enabled) OR (selectable with selection active)
  const showToolbar = (sortable && externalSortKey === undefined) || (selectable && selectedIds.size > 0)

  // Calculate max height for scrollable sections
  const getSectionMaxHeight = (sectionUsers) => {
    if (sectionUsers.length <= maxVisibleRows) return undefined
    return `${maxVisibleRows * ROW_HEIGHT}px`
  }

  return (
    <div>
      {/* Toolbar with sort and bulk actions - hidden when external sort controls and no selection */}
      {showToolbar && (
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/40">
          <div className="flex items-center gap-3">
            {selectable && (
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={toggleSelectAll}
              />
            )}
            {selectable && selectedIds.size > 0 && (
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {selectedIds.size} selected
              </span>
            )}
            {selectable && selectedIds.size > 0 && renderBulkActions && (
              <div className="flex items-center gap-2 ml-2">
                {renderBulkActions(selectedUsers)}
              </div>
            )}
          </div>
          {sortable && externalSortKey === undefined && (
            <div className="flex items-center gap-2">
              <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
              <select
                value={sortKey}
                onChange={e => setSortKey(e.target.value)}
                className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-navy-500/20"
              >
                {SORT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Single table with proper column alignment */}
      <div className="overflow-x-auto">
        <table className={`w-full table-fixed ${tableMinWidthClass}`}>
          {/* Column definitions for consistent widths */}
          <colgroup>
            {selectable && <col className="w-10" />}
            {columns.map(col => (
              <col key={col.key} className={col.colClassName || ''} />
            ))}
            {renderActions && <col className={actionsHeaderClassName.includes('w-') ? actionsHeaderClassName.split(' ').find(c => c.startsWith('w-')) : 'w-28'} />}
          </colgroup>

          {/* Table Header */}
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              {selectable && <th className={`${px} py-3 w-10`}></th>}
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`${px} py-3 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap ${col.headerClassName || ''}`}
                >
                  {col.label}
                </th>
              ))}
              {renderActions && <th className={`${px} py-3 ${actionsHeaderClassName}`}></th>}
            </tr>
          </thead>

          {/* Table Body with sections */}
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {SECTIONS.map(section => {
              const sectionUsers = sortedSectionedUsers[section.key] || []
              if (sectionUsers.length === 0) return null

              const isCollapsed = collapsedSections[section.key] || false
              const needsScroll = sectionUsers.length > maxVisibleRows

              return (
                <Fragment key={section.key}>
                  {renderSectionHeader(section)}
                  {!isCollapsed && (
                    needsScroll ? (
                      <tr>
                        <td colSpan={totalCols} className="p-0">
                          <div
                            className="overflow-y-auto"
                            style={{ maxHeight: getSectionMaxHeight(sectionUsers) }}
                          >
                            <table className="w-full table-fixed">
                              <colgroup>
                                {selectable && <col className="w-10" />}
                                {columns.map(col => (
                                  <col key={col.key} className={col.colClassName || ''} />
                                ))}
                                {renderActions && <col className={actionsHeaderClassName.includes('w-') ? actionsHeaderClassName.split(' ').find(c => c.startsWith('w-')) : 'w-28'} />}
                              </colgroup>
                              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                {sectionUsers.map(renderRow)}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      sectionUsers.map(renderRow)
                    )
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
